import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useConstants } from '../hooks/UseConstants';

function FormSideImageLayout({
    title,
    illustrationSrc,
    previewId,
    previewSrc,
    uploadLabel,
    onImageChange,
    onSubmit,
    submitLabel,
    loading,
    children,
}) {
    const theme = useTheme();
    const { language } = useConstants();
    const isEn = language === 'en';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: isEn ? 'row' : 'row-reverse' },
                gap: { xs: 3, md: 4 },
                p: { xs: 2, sm: 3, md: 4 },
                minHeight: { md: 'min(75vh, 720px)' },
            }}
        >
            <Box
                sx={{
                    flex: { xs: 'none', md: '0 0 38%' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    borderRadius: 3,
                    p: 3,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(160deg, #1a2744 0%, #121212 100%)'
                        : 'linear-gradient(160deg, #e8f0fa 0%, #f5f5f5 100%)',
                }}
            >
                <Box
                    component="img"
                    src={illustrationSrc}
                    alt=""
                    sx={{
                        width: '100%',
                        maxWidth: 280,
                        height: 'auto',
                        objectFit: 'contain',
                        mb: 1,
                    }}
                />
                <Typography variant="body2" color="text.secondary" className="!text-center">
                    {uploadLabel}
                </Typography>
                <Box
                    sx={{
                        position: 'relative',
                        width: 140,
                        height: 140,
                        borderRadius: '50%',
                        border: `2px dashed ${theme.palette.mode === 'dark' ? '#4b5563' : '#94a3b8'}`,
                        bgcolor: theme.palette.background.paper,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s',
                        '&:hover': { borderColor: '#0D358C' },
                    }}
                >
                    <AddPhotoAlternateIcon sx={{ color: 'text.secondary', fontSize: 40, zIndex: 0 }} />
                    <Box
                        component="img"
                        id={previewId}
                        src={previewSrc || undefined}
                        alt=""
                        sx={{
                            display: previewSrc ? 'block' : 'none',
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 1,
                        }}
                    />
                    <input
                        type="file"
                        accept="image/*"
                        onChange={onImageChange}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 2,
                        }}
                    />
                </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography
                    variant="h5"
                    className="!font-bold !mb-4"
                    sx={{ color: theme.palette.mode === 'dark' ? 'primary.light' : '#0D358C' }}
                >
                    {title}
                </Typography>
                <Box
                    className="none-view-scroll"
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        pr: { md: 1 },
                        maxHeight: { md: 'calc(75vh - 80px)' },
                    }}
                >
                    {children}
                </Box>
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        disabled={loading}
                        sx={{
                            minWidth: 160,
                            bgcolor: '#0D358C',
                            '&:hover': { bgcolor: '#0a2a6e' },
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : submitLabel}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export function previewImageFile(event, previewId) {
    const preview = document.getElementById(previewId);
    const file = event.target.files?.[0];
    if (!preview || !file) return;
    const reader = new FileReader();
    reader.addEventListener('load', function () {
        preview.style.display = 'block';
        preview.setAttribute('src', this.result);
    });
    reader.readAsDataURL(file);
}

export default FormSideImageLayout;
